import type { MessageReaction, ReactionClickHandler } from '../chatTypes';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';
import React, { useId, useState } from 'react';
import { useLayer } from 'react-laag';
import {
  DEFAULT_PLACEMENT_CONFIG,
  isTouchableDevice,
  mergeRefs
} from '../../../util';
import { getEmojiName } from './emojiNames';
import { Button } from '../../basic';
import { intl, intlFmt } from '../../../lib';
import { ReactionDetails } from './ReactionDetails';

type ReactionToolProps = {
  reaction: MessageReaction;
  eventId: string;
  roomId?: string;
  emojiData: EmojiData;
  reactionClick: ReactionClickHandler;
  openDrawer: () => void;
  action: 'openDrawer' | 'react';
  /**
   * Tab order of the reaction button. Reaction buttons of a chat message are
   * reached via the arrow keys (roving tabindex), so they pass -1 here.
   */
  tabIndex?: number;
  /** Ref to the underlying button, used for arrow-key focus management. */
  buttonRef?: React.Ref<HTMLButtonElement>;
  /** Disables toggling the reaction. The button stays focusable and details remain accessible. */
  disabled?: boolean;
};

export const ReactionTool = ({
  reaction,
  eventId,
  roomId,
  emojiData,
  reactionClick,
  openDrawer,
  action,
  tabIndex,
  buttonRef,
  disabled = false
}: ReactionToolProps) => {
  const elementId = useId();
  const isTouchDevice = isTouchableDevice();
  const [reactionDetailsOpen, setReactionDetailsOpen] = useState(false);
  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen: reactionDetailsOpen,
    onOutsideClick: !isTouchDevice
      ? () => setReactionDetailsOpen(false)
      : undefined,
    container: elementId,
    ...DEFAULT_PLACEMENT_CONFIG,
    placement: 'top-center'
  });
  const emojiName = getEmojiName(reaction.key, emojiData) ?? '';
  const emojiNameCapitalized =
    emojiName.charAt(0).toUpperCase() + emojiName.slice(1);
  const senders = reaction.sender
    .map(sender =>
      sender.isSender
        ? intl('common.you')
        : `${sender.firstname} ${sender.lastname}`.trim()
    )
    .join(', ');
  return (
    <>
      <Button
        {...triggerProps}
        id={elementId}
        variant="tertiary"
        className={`ehs-reactions chat-message-reaction${
          reaction.ownEventId ? ' own' : ''
        }`}
        aria-pressed={Boolean(reaction.ownEventId)}
        aria-disabled={disabled && action === 'react' ? true : undefined}
        aria-label={intlFmt(
          'common.chatmessage.reaction',
          emojiNameCapitalized ?? reaction.key,
          senders
        )}
        onClick={() => {
          if (action === 'react' && !disabled) {
            reactionClick(eventId, reaction.key, roomId);
          }
          if (action === 'openDrawer') {
            openDrawer();
          }
        }}
        text={reaction.key + (reaction.count > 1 ? ` ${reaction.count}` : '')}
        tabIndex={tabIndex}
        onMouseEnter={() => {
          if (!isTouchDevice) {
            setReactionDetailsOpen(true);
          }
        }}
        onMouseLeave={() => {
          if (!isTouchDevice) {
            setReactionDetailsOpen(false);
          }
        }}
        onFocus={() => {
          if (!isTouchDevice) {
            setReactionDetailsOpen(true);
          }
        }}
        onBlur={() => {
          if (!isTouchDevice) {
            setReactionDetailsOpen(false);
          }
        }}
        ref={mergeRefs([triggerProps.ref, buttonRef])} // need to override buttonprops
        data-testid="reaction-button"
      />
      {reactionDetailsOpen &&
        renderLayer(
          <div
            {...layerProps}
            style={{ ...layerProps.style }}
            ref={layerProps.ref}
            role="dialog"
            aria-modal="true"
            className="ehs-menu reaction-details"
            aria-hidden={!reactionDetailsOpen}
            tabIndex={-1}
          >
            <div className="menu-header">{emojiNameCapitalized}</div>
            <ReactionDetails
              reaction={reaction}
              showEmoji={false}
              emojiData={emojiData}
            />
          </div>
        )}
    </>
  );
};
